import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api } from "../utils/api";
import { t } from "../utils/i18n";
import { MOVE_MODE_ICONS, MoveMode } from "../components/move/MoveIcons";
import MoveCard, { MoveTrip, MOVE_MODE_I18N_KEY } from "../components/move/MoveCard";
import MoveDetailModal from "../components/move/MoveDetailModal";

const MODES: MoveMode[] = ["ride", "bike", "walk", "scoot"];

// Calqué sur PowerRequests.tsx ("Prestataires") — même structure (onglets À proximité/Mes
// trajets, filtres, liste, détail en modale) — mais la création se fait dans le flux "create"
// partagé (voir Create.tsx, domaine "move"), jamais une modale de création dédiée ici.
export default function Move() {
  const { user, lang } = useApp();
  const navigate = useNavigate();
  const [tab, setTab] = useState<"nearby" | "mine">("nearby");
  const [moves, setMoves] = useState<MoveTrip[]>([]);
  const [myMoves, setMyMoves] = useState<MoveTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [modeFilter, setModeFilter] = useState<MoveMode | "">("");
  const [detailId, setDetailId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    const params = modeFilter ? `?mode=${modeFilter}` : "";
    Promise.all([
      api.get(`/publications/moves/nearby${params}`).catch(() => ({ moves: [] })),
      user?.id ? api.get(`/users/${user.id}/publications`).catch(() => ({ publications: [] })) : Promise.resolve({ publications: [] }),
    ]).then(([nearby, mine]) => {
      setMoves(nearby?.moves || []);
      setMyMoves((mine?.publications || []).filter((p: any) => p.type === "move"));
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [modeFilter]);

  const list = tab === "nearby" ? moves : myMoves;

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "20px 24px 12px", borderBottom: "1px solid #F0F0F0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
            <span style={{ fontSize: 22, fontWeight: 700 }}>Move</span>
          </div>
          <button onClick={() => navigate("/create?move=1")} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t(lang, "move_cta_create")}</button>
        </div>
        <div style={{ padding: "10px 24px 0", fontSize: 13, color: "#999" }}>{t(lang, "move_page_subtitle")}</div>

        <div style={{ display: "flex", gap: 8, padding: "16px 24px 0" }}>
          <button onClick={() => setTab("nearby")} style={{ padding: "8px 16px", borderRadius: 20, border: tab === "nearby" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: tab === "nearby" ? "#0a0a0a" : "white", color: tab === "nearby" ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {t(lang, "move_tab_nearby")}
          </button>
          <button onClick={() => setTab("mine")} style={{ padding: "8px 16px", borderRadius: 20, border: tab === "mine" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: tab === "mine" ? "#0a0a0a" : "white", color: tab === "mine" ? "white" : "#666", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {t(lang, "move_tab_mine")} {myMoves.length > 0 ? `(${myMoves.length})` : ""}
          </button>
        </div>

        {tab === "nearby" && (
          <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, padding: "14px 24px 0", overflowX: "auto" }}>
            <style>{`.oltodo-no-scrollbar::-webkit-scrollbar{display:none} .oltodo-no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}`}</style>
            <button onClick={() => setModeFilter("")} style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: modeFilter === "" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: modeFilter === "" ? "#0a0a0a" : "white", color: modeFilter === "" ? "white" : "#666" }}>
              {t(lang, "move_filter_all_modes")}
            </button>
            {MODES.map(m => {
              const Icon = MOVE_MODE_ICONS[m];
              const active = modeFilter === m;
              return (
                <button key={m} onClick={() => setModeFilter(prev => prev === m ? "" : m)} style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", border: active ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: active ? "#0a0a0a" : "white", color: active ? "white" : "#666" }}>
                  <Icon size={14} color={active ? "white" : "#666"} />
                  {t(lang, MOVE_MODE_I18N_KEY[m])}
                </button>
              );
            })}
          </div>
        )}

        {loading && <div style={{ textAlign: "center", padding: 60, color: "#999" }}>{t(lang, "msg_loading")}</div>}
        {!loading && list.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
              <MOVE_MODE_ICONS.ride size={26} color="#999" />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{tab === "nearby" ? t(lang, "move_empty_nearby") : t(lang, "move_empty_mine")}</div>
          </div>
        )}

        {!loading && list.length > 0 && (
          <div style={{ padding: "8px 24px" }}>
            {list.map(m => <MoveCard key={m.id} move={m} lang={lang} onClick={() => setDetailId(m.id)} />)}
          </div>
        )}
      </div>

      {detailId && <MoveDetailModal moveId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}
